import isNil from 'lodash/isNil'
import { GeckoPublishingState } from 'types/cdfParams/geckoPublishingState'
import {
  ComponentDataFrameParams,
  getCDFParams,
  knownComponentDataFrameParamsKeys,
} from 'types/entries/geckoComponentDataFrame'
import { JsonValue } from 'types/json'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { formatDateLikeInspectionData } from 'utils/dateHelpers'
import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'
import { SortingPlacement } from 'utils/sortingHelpers'

import { GeckoEntryBase } from './geckoEntry'

/**
 * A component data set (CDS) represents a bundle of heatmap and A-scan data
 * for a given component which can be individually QA'd and finalized. This
 * means that there may be multiple CDSes for a single inspection.
 */
export class GeckoComponentDataset extends GeckoEntryBase<
  {
    publishing_state: GeckoPublishingState
    data_version: number
    metadata_flags: MetadataFlags
    data_frame_settings: { [key: string]: DataFrameSettings } | undefined
    ds_data_frame_settings: { [key: string]: DataFrameSettings } | undefined
    schema_version: number
    publishing_approvals: GeckoPublishingApprovals
  },
  {
    job: string
    site: string
    unit: string
    component: string
    inspection: string
    organization: string
  },
  Record<string, never>
> {
  // Private constructor ensures instances are only created via static method
  private constructor(json: RolodexEntryJson) {
    const rawPublishingState = parseString(json.data.publishing_state)
    const publishing_state =
      GeckoPublishingState.GetByID(rawPublishingState) ??
      GeckoPublishingState.Finalized
    const metadata_flags = json.data.metadata_flags as Record<string, boolean>
    const publishing_approvals = json.data.publishing_approvals as Record<
      string,
      unknown
    >

    // Call the parent constructor
    super(
      json,
      {
        publishing_state: publishing_state ?? GeckoPublishingState.Finalized,
        metadata_flags: {
          cscan_correction_run:
            parseBoolean(metadata_flags?.cscan_correction_run) ?? false,
          fast_bin_artifacts:
            parseBoolean(metadata_flags?.fast_bin_artifacts) ?? false,
          parquet_artifacts:
            parseBoolean(metadata_flags?.parquet_artifacts) ?? false,
          use_conduit: parseBoolean(metadata_flags?.use_conduit) ?? false,
        },
        data_version: parseNumber(json.data.data_version) ?? 0,
        data_frame_settings: isNil(json.data.data_frame_settings)
          ? undefined
          : generateDataFrameSettingsMap(
              json.data.data_frame_settings as Record<string, unknown>
            ),
        ds_data_frame_settings: isNil(json.data.ds_data_frame_settings)
          ? undefined
          : generateDataFrameSettingsMap(
              json.data.ds_data_frame_settings as Record<string, unknown>
            ),
        schema_version: parseNumber(json.data.schema_version) ?? 0,
        publishing_approvals: !publishing_approvals
          ? DefaultPublishingApprovals()
          : {
              qa_qc_lead: (publishing_approvals.qa_qc_lead as string) ?? '',
              supervisor: (publishing_approvals.supervisor as string) ?? '',
              data_lead: (publishing_approvals.data_lead as string) ?? '',
              sales_rep: (publishing_approvals.sales_rep as string) ?? '',
              custom_fields: new Map<string, string>(
                Object.entries(
                  (publishing_approvals.custom_fields as Record<
                    string,
                    string
                  >) ?? {}
                )
              ),
              custom_approvals: new Map<string, string>(
                Object.entries(
                  (publishing_approvals.custom_approvals as Record<
                    string,
                    string
                  >) ?? {}
                )
              ),
              notes: (publishing_approvals.notes as string) ?? '',
            },
      },
      {
        job: parseString(json.tags.job) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        inspection: parseString(json.tags.inspection) ?? '',
        organization: parseString(json.tags.organization) ?? '',
      },
      {}
    )
  }

  public get settingsMapKey(): 'data_frame_settings' | 'ds_data_frame_settings' {
    return this.data.metadata_flags.use_conduit
      ? 'data_frame_settings'
      : 'ds_data_frame_settings'
  }

  public generateCdfJson(
    columnName: string,
    df_settings:
      | {
          [key: string]: DataFrameSettings
        }
      | undefined
  ) {
    const columnSettings = df_settings?.[columnName]
    if (columnSettings === undefined) {
      throw new Error(`CDS column_settings undefined for column ${columnName}`)
    }
    return {
      binning: this.generateCdfBinningJson(columnSettings),
      geometry: this.generateCdfGeometryJson(columnSettings),
      params: this.generateCdfParamsJson(columnSettings),
      publishing_state: this.data.publishing_state.id,
      is_default: columnSettings.is_default ?? false,
      _parquet_column_name: columnName,
      parquet_file_name:
        columnSettings.parquet_file_name ?? this.determineParquetFilename(),
    }
  }

  private generateCdfBinningJson(columnSettings: DataFrameSettings) {
    return {
      y_offset: columnSettings.y_axis.bin_start_offset,
      x_offset: columnSettings.x_axis.bin_start_offset,
      bin_height: columnSettings.y_axis.bin_size,
      bin_width: columnSettings.x_axis.bin_size,
      x_bin_count: columnSettings.x_axis.length_in_bins,
      y_bin_count: columnSettings.y_axis.length_in_bins,
    }
  }

  private generateCdfGeometryJson(columnSettings: DataFrameSettings) {
    return {
      x_start: columnSettings.x_axis.start_offset,
      x_end:
        (columnSettings.x_axis.length_in_bins ?? 0) *
          columnSettings.x_axis.bin_size +
        columnSettings.x_axis.start_offset,
      x_measurement_unit: columnSettings.x_axis.measurement_unit.id,
      y_start: columnSettings.y_axis.start_offset,
      y_end:
        (columnSettings.y_axis.length_in_bins ?? 0) *
          columnSettings.y_axis.bin_size +
        columnSettings.y_axis.start_offset,
      y_measurement_unit: columnSettings.y_axis.measurement_unit.id,
    }
  }

  private generateCdfParamsJson(columnSettings: DataFrameSettings) {
    const knownParams = {
      view: columnSettings.view.id,
      source: columnSettings.source.id,
      algorithm: columnSettings.algorithm.id,
      aggregation: columnSettings.aggregation.id,
      angle_plot_setting: columnSettings.angle_plot_setting.id,
      tmin_method: columnSettings.tmin_method.id,
      measurement_unit: columnSettings.measurement_unit.id,
      date: isNil(columnSettings.date)
        ? undefined
        : formatDateLikeInspectionData(columnSettings.date),
      depth: columnSettings.depth,
      api_653_constants: columnSettings.api_653_constants,
    }

    const knownKeys = new Set<string>([
      ...Array.from(knownComponentDataFrameParamsKeys),
      ...Array.from(knownDataFrameSettingsKeys),
    ])

    const dynamicParams: Record<string, JsonValue> = Object.fromEntries(
      Object.entries(columnSettings)
        .filter(
          ([key, value]) =>
            !knownKeys.has(key) && value !== undefined && value !== null
        )
        .map(([key, value]) => [key, value as JsonValue])
    )

    return {
      ...knownParams,
      ...dynamicParams,
    }
  }

  private determineParquetFilename() {
    const CONDUIT_BINNED_DATA_FILENAME = 'binned.parquet'
    const DS_BINNED_DATA_FILENAME = 'ds_binned.parquet'

    return this.data.metadata_flags.use_conduit
      ? CONDUIT_BINNED_DATA_FILENAME
      : DS_BINNED_DATA_FILENAME
  }

  /**
   * A static factory method which creates an organization from raw JSON
   * @param json The raw JSON to create an instance from
   */
  public static CreateFromJSON(json: RolodexEntryJson): GeckoComponentDataset {
    return new GeckoComponentDataset(json)
  }

  /**
   * Default sorting function
   * @param a First GeckoComponentDataSet instance
   * @param b Second GeckoComponentDataSet instance
   */
  public static DefaultSort(
    a: GeckoComponentDataset,
    b: GeckoComponentDataset
  ): number {
    if (a.data.publishing_state !== b.data.publishing_state) {
      return GeckoPublishingState.DefaultSort(
        a.data.publishing_state,
        b.data.publishing_state
      )
    }

    return a.data.data_version > b.data.data_version
      ? SortingPlacement.Before
      : SortingPlacement.After
  }

  public static AreTagsValid(entry: GeckoComponentDataset) {
    return (
      entry.tags.organization &&
      entry.tags.site &&
      entry.tags.unit &&
      entry.tags.component &&
      entry.tags.inspection
    )
  }
}
export interface KnownDataFrameSettingsParams {
  x_axis: Axis
  y_axis: Axis
  ignore_cdf?: boolean | undefined
  parquet_file_name?: string | undefined
  is_default?: boolean | undefined
}

export type DataFrameSettings = ComponentDataFrameParams &
  KnownDataFrameSettingsParams

const DataFrameSettingsParameterNames = {
  X_AXIS: 'x_axis',
  Y_AXIS: 'y_axis',
  IGNORE_CDF: 'ignore_cdf',
  PARQUET_FILE_NAME: 'parquet_file_name',
  IS_DEFAULT: 'is_default',
} as const satisfies Record<string, keyof KnownDataFrameSettingsParams>

export const knownDataFrameSettingsKeys = new Set(
  Object.values(DataFrameSettingsParameterNames)
)

function generateDataFrameSettings(
  dfsJSON: Record<string, unknown>
): DataFrameSettings {
  const params = getCDFParams(dfsJSON)

  const xAxisJSON = dfsJSON.x_axis as Record<string, unknown>
  const yAxisJSON = dfsJSON.y_axis as Record<string, unknown>

  return {
    ignore_cdf: parseBoolean(dfsJSON.ignore_cdf),
    parquet_file_name: parseString(dfsJSON.parquet_file_name),
    is_default: parseBoolean(dfsJSON.is_default),
    x_axis: {
      start_offset: parseNumber(xAxisJSON.start_offset) ?? 0,
      bin_start_offset: parseNumber(xAxisJSON.bin_start_offset) ?? 0,
      bin_size: parseNumber(xAxisJSON.bin_size) ?? 0,
      measurement_unit: GeckoMeasurementUnit.GetOrCreate(
        parseString(xAxisJSON.measurement_unit) ?? ''
      ),
      length_in_bins: parseNumber(xAxisJSON.length_in_bins),
    },
    y_axis: {
      start_offset: parseNumber(yAxisJSON.start_offset) ?? 0,
      bin_start_offset: parseNumber(yAxisJSON.bin_start_offset) ?? 0,
      bin_size: parseNumber(yAxisJSON.bin_size) ?? 0,
      measurement_unit: GeckoMeasurementUnit.GetOrCreate(
        parseString(yAxisJSON.measurement_unit) ?? ''
      ),
      length_in_bins: parseNumber(yAxisJSON.length_in_bins),
    },

    ...params,
  }
}

function generateDataFrameSettingsMap(dfsMapJSON: Record<string, unknown>): {
  [key: string]: DataFrameSettings
} {
  return Object.entries(dfsMapJSON).reduce<Record<string, DataFrameSettings>>(
    (agg, [key, value]) => {
      if (typeof value !== 'object') {
        throw new Error(
          `Invalid data_frame_settings for column ${key}: expected object, got ${typeof value}`
        )
      }
      return {
        ...agg,
        [key]: generateDataFrameSettings(value as Record<string, unknown>),
      }
    },
    {}
  )
}

export type GeckoPublishingApprovalsKnownField =
  | 'data_lead'
  | 'qa_qc_lead'
  | 'sales_rep'
  | 'supervisor'

export interface GeckoPublishingApprovals {
  qa_qc_lead: string
  supervisor: string
  data_lead: string
  sales_rep: string
  custom_fields: Map<string, string>
  custom_approvals: Map<string, string>
  notes: string
}

function DefaultPublishingApprovals() {
  return {
    qa_qc_lead: '',
    supervisor: '',
    data_lead: '',
    sales_rep: '',
    custom_fields: new Map<string, string>(),
    custom_approvals: new Map<string, string>(),
    notes: '',
  } as GeckoPublishingApprovals
}

interface Axis {
  start_offset: number
  bin_start_offset: number
  bin_size: number
  measurement_unit: GeckoMeasurementUnit
  length_in_bins: number | undefined
}

interface MetadataFlags {
  cscan_correction_run: boolean
  fast_bin_artifacts: boolean
  parquet_artifacts: boolean
  use_conduit: boolean
}
